"use client";

import type { AddonDietAddonsRead } from "@repo/api/catalog/api/schemas/addon-diet-addons-read";
import type { DietAddonGroupDietAddonsRead } from "@repo/api/catalog/api/schemas/diet-addon-group-diet-addons-read";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import type { SelectedAddon } from "@/features/addons/lib/aggregate-selected-addons";
import { AddonCategoryTabs, addonTabClassName } from "./addon-category-tabs";
import { DietAddonsAvailableList } from "./diet-addons-available-list";
import { DietAddonsSelectedList } from "./diet-addons-selected-list";

// Mobile-only sentinel tab that shows the selected addons instead of a category.
const SELECTED_TAB = "__selected__";

const byPosition = <T extends { position: number }>(a: T, b: T) =>
  a.position - b.position;

type DietAddonsSummaryContentProps = {
  categories: DietAddonGroupDietAddonsRead[];
  items: SelectedAddon[];
  daysCount?: number;
  isMobile: boolean;
  onAddAddon: (addon: AddonDietAddonsRead) => void;
  onEditAddon: (addonId: string) => void;
  onRemoveAddon: (addonId: string) => void;
  onRemoveAll: () => void;
  cateringName?: string;
  isVisible?: boolean;
};

// Summary view body: two columns on desktop (available | selected), a single
// tabbed panel on mobile where "Twoje dodatki" is the first tab.
export const DietAddonsSummaryContent = ({
  categories,
  items,
  daysCount,
  isMobile,
  onAddAddon,
  onEditAddon,
  onRemoveAddon,
  onRemoveAll,
  cateringName,
  isVisible,
}: DietAddonsSummaryContentProps) => {
  const t = useTranslations();

  const sortedCategories = useMemo(
    () => [...categories].sort(byPosition),
    [categories],
  );
  const firstCategorySlug = sortedCategories.at(0)?.slug ?? "";

  const [activeCategory, setActiveCategory] = useState(firstCategorySlug);
  // Mobile starts on the "Twoje dodatki" tab, matching the design's default.
  const [mobileTab, setMobileTab] = useState(SELECTED_TAB);

  if (isMobile) {
    const isSelectedTab = mobileTab === SELECTED_TAB;

    return (
      <div className="flex flex-col">
        <AddonCategoryTabs
          activeCategory={mobileTab}
          categories={sortedCategories}
          className="bg-brand-white"
          leadingTab={
            <button
              className={addonTabClassName(isSelectedTab)}
              onClick={() => setMobileTab(SELECTED_TAB)}
              type="button"
            >
              {t("cart.configuration.addons.summary.selectedHeading", {
                count: items.length,
              })}
            </button>
          }
          onSelectCategory={(slug) => {
            setMobileTab(slug);
            setActiveCategory(slug);
          }}
        />

        <div className="flex flex-1 flex-col p-4">
          {isSelectedTab ? (
            <DietAddonsSelectedList
              cateringName={cateringName}
              emptyDescription={t(
                "cart.configuration.addons.summary.emptyDescriptionMobile",
              )}
              items={items}
              onEdit={onEditAddon}
              onRemove={onRemoveAddon}
              onRemoveAll={onRemoveAll}
              showHeader={false}
            />
          ) : (
            <DietAddonsAvailableList
              activeCategory={activeCategory}
              cateringName={cateringName}
              categories={sortedCategories}
              daysCount={daysCount}
              isVisible={isVisible}
              onAddAddon={onAddAddon}
              onSelectCategory={setActiveCategory}
              showTabs={false}
            />
          )}
        </div>
      </div>
    );
  }

  // `min-w-0` on both the row and the left column: without it the category tabs'
  // intrinsic width would widen the columns past the dialog.
  return (
    <div className="flex min-h-0 min-w-0 flex-1">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col border-brand-grey2 border-r bg-white p-4 md:p-6">
        <DietAddonsAvailableList
          activeCategory={activeCategory}
          cateringName={cateringName}
          categories={sortedCategories}
          daysCount={daysCount}
          isVisible={isVisible}
          onAddAddon={onAddAddon}
          onSelectCategory={setActiveCategory}
          showTabs
        />
      </div>
      <div className="flex min-h-0 w-94 shrink-0 flex-col overflow-y-auto p-4">
        <DietAddonsSelectedList
          cateringName={cateringName}
          emptyDescription={t(
            "cart.configuration.addons.summary.emptyDescriptionDesktop",
          )}
          items={items}
          onEdit={onEditAddon}
          onRemove={onRemoveAddon}
          onRemoveAll={onRemoveAll}
          showHeader
        />
      </div>
    </div>
  );
};
